/**
 * Admin Mailed Card Order Detail Modal
 *
 * Shows full order information when an admin clicks a row in the mailed card orders table.
 * Fetches detail from GET /api/admin/mailed-card-orders/{id}, which logs an audit event
 * for PII access accountability (recipient and sender addresses).
 *
 * Contextual recovery actions:
 * - Cancel Order: visible when the pre-order is cancellable (Pending + None/Authorized)
 * - Retry Capture: visible only for Placed/CaptureFailed orders
 * - Retry Place: visible for Failed/Voided without a Cardly order id (PBI #1157)
 * - Void Authorization: visible only for Pending/Authorized orders
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Text,
  Group,
  Stack,
  Badge,
  Divider,
  Loader,
  Center,
  Alert,
  Button,
  Image,
  Paper,
  Anchor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AppModal } from '../common/AppModal';
import { useModalZIndex } from '../../hooks/useModalZIndex';
import {
  IconAlertCircle,
  IconRefresh,
  IconBan,
  IconRobot,
  IconRestore,
  IconX,
} from '@tabler/icons-react';
import apiClient from '../../services/apiClient';
import { actionColors, brandColors, semanticColors } from '../../config/theme';
import { formatCalendarDate, formatInstantLocalShort } from '../../utils/formatDateTime';
import { FulfillmentStatus, PaymentStatus } from '../../types';
import { OrderStatusBadges } from '../common/OrderStatusBadges';
import {
  canAdminRetryPlaceMailedCardOrder,
  canCancelPaidOrder,
  formatRetryingBadgeLabel,
  formatStatusLabel,
  RETRYING_PLACEMENT_BADGE_COLOR,
} from '../../utils/orderStatusDisplay';
import type { AdminMailedCardOrder } from '../../pages/admin/AdminMailedCardOrdersPage';
import { getStateName } from '../../constants/states';
import { DemoAccountPill } from './DemoAccountPill';

interface AdminMailedCardOrderDetailModalProps {
  orderId: number | null;
  opened: boolean;
  onClose: () => void;
  onOrderUpdated?: () => void;
}

const formatDate = (dateStr: string | null | undefined): string =>
  formatCalendarDate(dateStr);

const formatDateTime = (dateStr: string | null | undefined): string =>
  formatInstantLocalShort(dateStr);

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '--';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Group justify="space-between" wrap="nowrap">
    <Text fw={500} size="sm" style={{ flexShrink: 0 }}>
      {label}
    </Text>
    <div style={{ textAlign: 'right' }}>{children}</div>
  </Group>
);

export const AdminMailedCardOrderDetailModal = ({
  orderId,
  opened,
  onClose,
  onOrderUpdated,
}: AdminMailedCardOrderDetailModalProps) => {
  const zIndex = useModalZIndex(opened);
  const [order, setOrder] = useState<AdminMailedCardOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    'retry' | 'retry-place' | 'void' | 'cancel' | null
  >(null);

  const fetchDetail = useCallback(async (showLoader: boolean) => {
    if (!orderId) return;

    if (showLoader) setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get<AdminMailedCardOrder>(
        `/admin/mailed-card-orders/${orderId}`
      );
      setOrder(response.data);
    } catch {
      setError('Failed to load order details.');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!opened || !orderId) {
      setOrder(null);
      setError(null);
      setConfirmAction(null);
      return;
    }

    void fetchDetail(true);
  }, [opened, orderId, fetchDetail]);

  /** Reloads detail after a recovery action; keeps the modal open for status inspection. */
  const refreshAfterAction = async () => {
    onOrderUpdated?.();
    await fetchDetail(false);
  };

  const handleRetryCapture = async () => {
    if (!order) return;
    setActionLoading(true);

    try {
      await apiClient.post(`/admin/mailed-card-orders/${order.id}/retry-capture`);
      notifications.show({
        title: 'Capture Succeeded',
        message: `Order #${order.id} payment status updated to Captured.`,
        color: brandColors.success,
      });
      await refreshAfterAction();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Retry capture failed. Check the Stripe dashboard.';
      notifications.show({
        title: 'Capture Failed',
        message,
        color: brandColors.danger,
      });
      await refreshAfterAction();
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  /**
   * Admin Retry Place (PBI #1157): reset Failed/Voided, re-authorize Stripe, place with Cardly.
   * Keeps the modal open so ops can see Placed/Captured or the next failure immediately.
   */
  const handleRetryPlace = async () => {
    if (!order) return;
    setActionLoading(true);

    try {
      const response = await apiClient.post<{ message?: string }>(
        `/admin/mailed-card-orders/${order.id}/retry-place`
      );
      notifications.show({
        title: 'Order Placed',
        message: response.data.message || `Mailed card order #${order.id} was placed.`,
        color: brandColors.success,
      });
      await refreshAfterAction();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Retry place failed.';
      notifications.show({
        title: 'Retry Place Failed',
        message,
        color: brandColors.danger,
      });
      await refreshAfterAction();
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleCancelOrder = async () => {
    if (!order) return;
    setActionLoading(true);

    try {
      await apiClient.post(`/admin/mailed-card-orders/${order.id}/cancel`);
      notifications.show({
        title: 'Order Cancelled',
        message: `Mailed card order #${order.id} has been cancelled.`,
        color: brandColors.success,
      });
      await refreshAfterAction();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Cancel failed.';
      notifications.show({
        title: 'Cancel Failed',
        message,
        color: brandColors.danger,
      });
      await refreshAfterAction();
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleVoidAuthorization = async () => {
    if (!order) return;
    setActionLoading(true);

    try {
      await apiClient.post(`/admin/mailed-card-orders/${order.id}/void-authorization`);
      notifications.show({
        title: 'Authorization Voided',
        message: `Order #${order.id} status updated to Failed.`,
        color: brandColors.success,
      });
      await refreshAfterAction();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Void failed. Check the Stripe dashboard.';
      notifications.show({
        title: 'Void Failed',
        message,
        color: brandColors.danger,
      });
      await refreshAfterAction();
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const formatRecipientAddress = (): string => {
    if (!order) return '--';
    const parts = [
      order.addressLine1,
      order.addressLine2,
      [order.city, getStateName(order.state, order.country)].filter(Boolean).join(', '),
      order.postalCode,
      order.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join('\n') : '--';
  };

  const formatSenderAddress = (): string => {
    if (!order) return '--';
    const parts = [
      order.senderAddressLine1,
      order.senderAddressLine2,
      [order.senderCity, getStateName(order.senderState, order.senderCountry)].filter(Boolean).join(', '),
      order.senderPostalCode,
      order.senderCountry,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join('\n') : '--';
  };

  const recipientName = order
    ? `${order.recipientFirstName} ${order.recipientLastName}`.trim()
    : '';

  const senderName = order
    ? `${order.senderFirstName} ${order.senderLastName}`.trim()
    : '';

  const showRetryCapture =
    order?.fulfillmentStatus === FulfillmentStatus.Placed
    && order?.paymentStatus === PaymentStatus.CaptureFailed;

  const showRetryPlace =
    order != null
    && !order.cardlyOrderId
    && canAdminRetryPlaceMailedCardOrder(order.fulfillmentStatus, order.paymentStatus);

  const showVoidAuth =
    order?.fulfillmentStatus === FulfillmentStatus.Pending
    && order?.paymentStatus === PaymentStatus.Authorized;

  const showCancelOrder =
    order != null
    && canCancelPaidOrder(order.fulfillmentStatus, order.paymentStatus);

  const actionFooter =
    order && (showCancelOrder || showRetryCapture || showRetryPlace || showVoidAuth) ? (
      <Group justify="flex-end">
        {showCancelOrder && (
          <Button
            leftSection={<IconX size={16} />}
            color={actionColors.cancel}
            variant="light"
            disabled={actionLoading}
            onClick={() => setConfirmAction('cancel')}
          >
            Cancel Order
          </Button>
        )}
        {showRetryPlace && (
          <Button
            leftSection={<IconRestore size={16} />}
            color={actionColors.restore}
            variant="light"
            disabled={actionLoading}
            onClick={() => setConfirmAction('retry-place')}
          >
            Retry Place
          </Button>
        )}
        {showRetryCapture && (
          <Button
            leftSection={<IconRefresh size={16} />}
            color={actionColors.resend}
            variant="light"
            disabled={actionLoading}
            onClick={() => setConfirmAction('retry')}
          >
            Retry Capture
          </Button>
        )}
        {showVoidAuth && (
          <Button
            leftSection={<IconBan size={16} />}
            color={actionColors.delete}
            variant="light"
            disabled={actionLoading}
            onClick={() => setConfirmAction('void')}
          >
            Void Authorization
          </Button>
        )}
      </Group>
    ) : null;

  const confirmOpened = confirmAction != null;
  const confirmZIndex = useModalZIndex(confirmOpened);

  const confirmTitle =
    confirmAction === 'cancel'
      ? 'Confirm Cancel Order'
      : confirmAction === 'retry'
        ? 'Confirm Retry Capture'
        : confirmAction === 'retry-place'
          ? 'Confirm Retry Place'
          : confirmAction === 'void'
            ? 'Confirm Void Authorization'
            : 'Confirm';

  const confirmFooter =
    order && confirmAction === 'cancel' ? (
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={() => setConfirmAction(null)}
          disabled={actionLoading}
        >
          Keep Order
        </Button>
        <Button color={actionColors.cancel} onClick={handleCancelOrder} loading={actionLoading}>
          Yes, Cancel Order
        </Button>
      </Group>
    ) : order && confirmAction === 'retry' ? (
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={() => setConfirmAction(null)}
          disabled={actionLoading}
        >
          Cancel
        </Button>
        <Button color={actionColors.sendNow} onClick={handleRetryCapture} loading={actionLoading}>
          Yes, Capture
        </Button>
      </Group>
    ) : order && confirmAction === 'retry-place' ? (
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={() => setConfirmAction(null)}
          disabled={actionLoading}
        >
          Cancel
        </Button>
        <Button color={actionColors.restore} onClick={handleRetryPlace} loading={actionLoading}>
          Yes, Retry Place
        </Button>
      </Group>
    ) : order && confirmAction === 'void' ? (
      <Group justify="flex-end">
        <Button
          variant="default"
          onClick={() => setConfirmAction(null)}
          disabled={actionLoading}
        >
          Cancel
        </Button>
        <Button color={actionColors.delete} onClick={handleVoidAuthorization} loading={actionLoading}>
          Yes, Void
        </Button>
      </Group>
    ) : null;

  return (
    <>
    <AppModal
      id="admin-mailed-card-order-detail"
      opened={opened}
      onClose={onClose}
      zIndex={zIndex}
      title={order ? `Mailed Card Order #${order.id}` : 'Order Details'}
      size="lg"
      footer={actionFooter}
    >
      {loading && (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      )}

      {error && (
        <Alert color={brandColors.danger} icon={<IconAlertCircle size={16} />}>
          {error}
        </Alert>
      )}

      {order && !loading && (
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="sm">
              <OrderStatusBadges
                fulfillmentStatus={order.fulfillmentStatus}
                paymentStatus={order.paymentStatus}
                size="lg"
              />
              {order.isAutoScheduled && (
                <Badge color="blue" size="sm" variant="outline" leftSection={<IconRobot size={12} />}>
                  AutoSend
                </Badge>
              )}
            </Group>
            {order.cardlyOrderId && (
              <Text size="sm" c={semanticColors.text.secondary}>
                Cardly #{order.cardlyOrderId}
              </Text>
            )}
          </Group>

          <Divider label="Artwork" labelPosition="left" />
          <Group align="flex-start" gap="md">
            {order.artworkPreviewUrl && (
              <Image
                src={order.artworkPreviewUrl}
                alt={order.artworkName}
                w={100}
                h={100}
                radius="md"
                fit="contain"
              />
            )}
            <Stack gap={4}>
              <Text fw={600}>{order.artworkName}</Text>
              <Text size="sm" c={semanticColors.text.secondary}>Slug: {order.artworkSlug}</Text>
              <Text size="sm">Retail price: {formatCurrency(order.priceUsd)}</Text>
              <Text size="sm">Credit cost: {order.creditCost}</Text>
              <Text size="sm">Shipping: {order.shippingMethod}</Text>
            </Stack>
          </Group>

          <Divider label="Pricing" labelPosition="left" />
          <Paper p="sm" withBorder radius="sm">
            <Stack gap={4}>
              <DetailRow label="Retail Price">
                <Text size="sm">{formatCurrency(order.priceUsd)}</Text>
              </DetailRow>
              {(order.quotedTaxUsd != null || order.chargedTaxUsd != null) && (
                <>
                  {order.quotedTaxUsd != null && (
                    <DetailRow label="Quoted Tax">
                      <Text size="sm">{formatCurrency(order.quotedTaxUsd)}</Text>
                    </DetailRow>
                  )}
                  {order.chargedTaxUsd != null && (
                    <DetailRow label="Charged Tax">
                      <Text size="sm">{formatCurrency(order.chargedTaxUsd)}</Text>
                    </DetailRow>
                  )}
                  {order.taxAddressSource && (
                    <DetailRow label="Tax Address Source">
                      <Text size="sm">{order.taxAddressSource}</Text>
                    </DetailRow>
                  )}
                  {order.stripeTaxCalculationId && (
                    <DetailRow label="Stripe Tax Calculation">
                      <Text size="xs" ff="monospace" c={semanticColors.text.secondary}>
                        {order.stripeTaxCalculationId}
                      </Text>
                    </DetailRow>
                  )}
                </>
              )}
              {order.authorizedAmount != null && (
                <DetailRow label="Authorized Total">
                  <Text size="sm" fw={600}>{formatCurrency(order.authorizedAmount)}</Text>
                </DetailRow>
              )}
              {order.capturedAmount != null && (
                <DetailRow label="Captured Amount">
                  <Text size="sm" fw={600} c={brandColors.success}>
                    {formatCurrency(order.capturedAmount)}
                  </Text>
                </DetailRow>
              )}
            </Stack>
          </Paper>

          <Divider label="Payment" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Payment Status">
              <Text size="sm">{formatStatusLabel(order.paymentStatus)}</Text>
            </DetailRow>
            <DetailRow label="Fulfillment Status">
              <Group gap="xs">
                <Text size="sm">{formatStatusLabel(order.fulfillmentStatus)}</Text>
                {formatRetryingBadgeLabel(order) && (
                  <Badge size="sm" color={RETRYING_PLACEMENT_BADGE_COLOR} variant="light">
                    {formatRetryingBadgeLabel(order)}
                  </Badge>
                )}
              </Group>
            </DetailRow>
            {order.lastPlacementError && (
              <DetailRow label="Last Placement Error">
                <Text size="xs" c={semanticColors.text.secondary}>
                  {order.lastPlacementError}
                </Text>
              </DetailRow>
            )}
            {order.cardLast4 && (
              <DetailRow label="Card">
                <Text size="sm">•••• {order.cardLast4}</Text>
              </DetailRow>
            )}
            {order.stripePaymentIntentId && (
              <DetailRow label="Stripe PI">
                <Text size="xs" ff="monospace" c={semanticColors.text.secondary}>
                  {order.stripePaymentIntentId}
                </Text>
              </DetailRow>
            )}
          </Stack>

          {(order.trackingCarrier || order.trackingCode || order.trackingUrl || order.cardlyStatusRaw) && (
            <>
              <Divider label="Tracking" labelPosition="left" />
              <Stack gap={4}>
                {order.cardlyStatusRaw && (
                  <DetailRow label="Cardly Status">
                    <Text size="sm">{order.cardlyStatusRaw}</Text>
                  </DetailRow>
                )}
                {order.trackingCarrier && (
                  <DetailRow label="Carrier">
                    <Text size="sm">{order.trackingCarrier}</Text>
                  </DetailRow>
                )}
                {order.trackingCode && (
                  <DetailRow label="Tracking Code">
                    <Text size="sm">{order.trackingCode}</Text>
                  </DetailRow>
                )}
                {order.trackingUrl && (
                  <DetailRow label="Tracking URL">
                    <Anchor href={order.trackingUrl} target="_blank" rel="noopener noreferrer" size="sm">
                      Open tracking
                    </Anchor>
                  </DetailRow>
                )}
                {order.lodgedAtUtc && (
                  <DetailRow label="Lodged">
                    <Text size="sm">{formatDateTime(order.lodgedAtUtc)}</Text>
                  </DetailRow>
                )}
                {order.shippedAtUtc && (
                  <DetailRow label="Shipped">
                    <Text size="sm">{formatDateTime(order.shippedAtUtc)}</Text>
                  </DetailRow>
                )}
              </Stack>
            </>
          )}

          <Divider label="Recipient" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Name">
              <Text size="sm">{recipientName}</Text>
            </DetailRow>
            <DetailRow label="Address">
              <Text size="sm" style={{ whiteSpace: 'pre-line', textAlign: 'right' }}>
                {formatRecipientAddress()}
              </Text>
            </DetailRow>
            {order.message && (
              <DetailRow label="Message">
                <Text size="sm" fs="italic" style={{ maxWidth: 250, textAlign: 'right' }}>
                  {order.message}
                </Text>
              </DetailRow>
            )}
          </Stack>

          <Divider label="Sender (Return Address)" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Name">
              <Text size="sm">{senderName}</Text>
            </DetailRow>
            <DetailRow label="Address">
              <Text size="sm" style={{ whiteSpace: 'pre-line', textAlign: 'right' }}>
                {formatSenderAddress()}
              </Text>
            </DetailRow>
          </Stack>

          <Divider label="Event & Dates" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Event Type">
              <Text size="sm">{order.eventTypeName}</Text>
            </DetailRow>
            <DetailRow label="Requested Arrival">
              <Text size="sm">{formatDate(order.requestedArrival)}</Text>
            </DetailRow>
          </Stack>

          <Divider label="Customer" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Name">
              <Text size="sm">{order.customerName || '--'}</Text>
            </DetailRow>
            <DetailRow label="Email">
              <Text size="sm">{order.customerEmail}</Text>
            </DetailRow>
            {order.isDemoAccount && (
              <DetailRow label="Account">
                <DemoAccountPill size="sm" />
              </DetailRow>
            )}
          </Stack>

          <Divider label="Timestamps" labelPosition="left" />
          <Stack gap={4}>
            <DetailRow label="Created">
              <Text size="sm">{formatDateTime(order.createdAt)}</Text>
            </DetailRow>
          </Stack>
        </Stack>
      )}
    </AppModal>

    <AppModal
      id="admin-mailed-card-order-action-confirm"
      opened={confirmOpened && order != null}
      onClose={() => {
        if (!actionLoading) setConfirmAction(null);
      }}
      zIndex={confirmZIndex}
      title={confirmTitle}
      size="sm"
      bodyHeight="auto"
      footer={confirmFooter}
    >
      {order && confirmAction === 'cancel' && (
        <Text size="sm">
          This will cancel the pre-order
          {order.paymentStatus === PaymentStatus.Authorized
            ? ` and release the ${formatCurrency(order.authorizedAmount)} hold on the customer's card`
            : ''}
          . The linked scheduled item will also be cancelled.
        </Text>
      )}
      {order && confirmAction === 'retry' && (
        <Text size="sm">
          This will attempt to capture {formatCurrency(order.authorizedAmount)} from the customer's
          card. The Cardly order has already been placed. Proceed?
        </Text>
      )}
      {order && confirmAction === 'retry-place' && (
        <Text size="sm">
          This will create a new Stripe authorization on the customer&apos;s saved payment method,
          place the order with Cardly, and capture payment if placement succeeds. Proceed only after
          Cardly account credit (and any other vendor issues) are resolved.
        </Text>
      )}
      {order && confirmAction === 'void' && (
        <Text size="sm">
          This will release the {formatCurrency(order.authorizedAmount)} hold on the customer's card
          and mark the order as Failed. This cannot be undone.
        </Text>
      )}
    </AppModal>
    </>
  );
};
